// ScriptExecutionTimeline.jsx - Improved Gantt-style duration visualization
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Modal, Select, Button, Segmented } from 'antd';
import { Tooltip as MuiTooltip } from '@mui/material';

const ScriptExecutionTimeline = ({
  scriptExecVisible,
  setScriptExecVisible,
  scriptExecData,
}) => {
  const [scale, setScale] = useState('24h');
  const [viewMode, setViewMode] = useState('timeline'); // 'timeline' or 'duration'
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1000);

  // Measure container width
  useEffect(() => {
    if (containerRef.current) {
      const width = containerRef.current.offsetWidth;
      if (width > 0) setContainerWidth(width);
    }
  }, [scriptExecVisible]);

  // Current time for filtering
  const now = Date.now();
  const filteredData = useMemo(() => {
    const cutoffs = {
      '1h': now - 60 * 60 * 1000,
      '4h': now - 4 * 60 * 60 * 1000,
      '24h': now - 24 * 60 * 60 * 1000,
    };
    if (cutoffs[scale]) {
      return scriptExecData.filter(
        (item) => new Date(item.start_time).getTime() >= cutoffs[scale]
      );
    }
    return scriptExecData;
  }, [scriptExecData, scale, now]);

  // Edge: nothing to show
  if (!scriptExecVisible) return null;

  // Calculate end time for each item (use duration_seconds if end_time missing)
  const dataWithEnd = filteredData.map((item) => {
    const startMs = new Date(item.start_time).getTime();
    let endMs;
    
    if (item.end_time) {
      endMs = new Date(item.end_time).getTime();
    } else if (item.duration_seconds > 0) {
      endMs = startMs + (item.duration_seconds * 1000);
    } else {
      endMs = now; // Still running
    }
    
    const durationMs = endMs - startMs;
    return {
      ...item,
      startMs,
      endMs,
      durationMs,
      durationMin: durationMs / 60000,
      durationSec: durationMs / 1000,
      isIncomplete: !item.end_time,
    };
  });

  // Sort by duration for duration view, by start time for timeline
  const sortedData = viewMode === 'duration'
    ? [...dataWithEnd].sort((a, b) => b.durationMs - a.durationMs)
    : dataWithEnd;

  // min/max for timeline
  const minTime = dataWithEnd.length > 0
    ? Math.min(...dataWithEnd.map((item) => item.startMs))
    : 0;
  const maxTime = dataWithEnd.length > 0
    ? Math.max(...dataWithEnd.map((item) => item.endMs))
    : 0;
  const totalMs = maxTime - minTime || 1;
  
  // For duration view, find max duration
  const maxDuration = dataWithEnd.length > 0
    ? Math.max(...dataWithEnd.map((item) => item.durationMs))
    : 1;

  return (
    <Modal
      open={scriptExecVisible}
      onCancel={() => setScriptExecVisible(false)}
      footer={null}
      width="80vw"
      getContainer={false}
      style={{ top: 40 }}
      maskClosable
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <h3 style={{ fontWeight: 'bold', margin: 0 }}>Execution Timeline</h3>
        <Segmented
          size="small"
          value={viewMode}
          onChange={setViewMode}
          options={[
            { label: 'Timeline', value: 'timeline' },
            { label: 'By Duration', value: 'duration' },
          ]}
        />
        <Select
          value={scale}
          style={{ width: 140, fontSize: 12 }}
          size="small"
          onChange={setScale}
          getPopupContainer={() => document.body}
          dropdownStyle={{ zIndex: 9999 }}
        >
          <Select.Option value="1h">Last 1 Hour</Select.Option>
          <Select.Option value="4h">Last 4 Hours</Select.Option>
          <Select.Option value="24h">Last 24 Hours</Select.Option>
          <Select.Option value="full">Full Timeline</Select.Option>
        </Select>
        <div style={{ fontSize: 10, color: '#666', display: 'flex', gap: 6 }}>
          <span style={{ backgroundColor: '#4dabf7', padding: '2px 6px', borderRadius: 3, color: '#fff' }}>&lt;30m</span>
          <span style={{ backgroundColor: '#ffe066', padding: '2px 6px', borderRadius: 3 }}>30m-1h</span>
          <span style={{ backgroundColor: '#ffa94d', padding: '2px 6px', borderRadius: 3, color: '#fff' }}>&gt;1h</span>
          <span style={{ backgroundColor: '#ff6b6b', padding: '2px 6px', borderRadius: 3, color: '#fff', border: '1px dashed #c92a2a' }}>Incomplete</span>
        </div>
      </div>
      <div style={{ margin: '16px 0' }} ref={containerRef}>
        {sortedData.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#999' }}>
            No execution data for selected range.
          </div>
        ) : viewMode === 'duration' ? (
          /* DURATION VIEW - Horizontal bar chart sorted by duration */
          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {sortedData.map((item, index) => {
              const barWidthPct = (item.durationMs / maxDuration) * 100;
              
              // Color based on duration
              let bgColor = '#4dabf7';
              if (item.isIncomplete) bgColor = '#ff6b6b';
              else if (item.durationMin > 60) bgColor = '#ffa94d';
              else if (item.durationMin > 30) bgColor = '#ffe066';

              // Format duration nicely
              const formatDuration = (ms) => {
                const sec = Math.floor(ms / 1000);
                if (sec < 60) return `${sec}s`;
                const min = Math.floor(sec / 60);
                if (min < 60) return `${min}m ${sec % 60}s`;
                const hr = Math.floor(min / 60);
                return `${hr}h ${min % 60}m`;
              };

              return (
                <div
                  key={item.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: 4,
                    fontSize: 11,
                  }}
                >
                  <div style={{ width: 200, flexShrink: 0, paddingRight: 8, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.script_name}
                  </div>
                  <MuiTooltip
                    title={
                      <span>
                        <b>{item.script_name}</b><br />
                        Start: {item.start_time}<br />
                        End: {item.end_time || '(incomplete)'}<br />
                        Duration: {formatDuration(item.durationMs)}
                      </span>
                    }
                    arrow
                    placement="top"
                  >
                    <div
                      style={{
                        width: `${Math.max(barWidthPct, 2)}%`,
                        minWidth: 40,
                        height: 18,
                        backgroundColor: bgColor,
                        borderRadius: 3,
                        display: 'flex',
                        alignItems: 'center',
                        paddingLeft: 6,
                        color: bgColor === '#ffe066' ? '#333' : '#fff',
                        fontSize: 10,
                        fontWeight: 500,
                        cursor: 'pointer',
                        border: item.isIncomplete ? '1px dashed #c92a2a' : 'none',
                      }}
                    >
                      {formatDuration(item.durationMs)}
                    </div>
                  </MuiTooltip>
                </div>
              );
            })}
          </div>
        ) : (
          /* TIMELINE VIEW - Gantt-style with proper positioning */
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: 1200, position: 'relative' }}>
              {/* Time axis at top */}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#666', marginBottom: 8, padding: '0 200px 0 0' }}>
                <span>{new Date(minTime).toLocaleString()}</span>
                <span>{new Date(minTime + totalMs * 0.25).toLocaleTimeString()}</span>
                <span>{new Date(minTime + totalMs * 0.5).toLocaleTimeString()}</span>
                <span>{new Date(minTime + totalMs * 0.75).toLocaleTimeString()}</span>
                <span>{new Date(maxTime).toLocaleString()}</span>
              </div>
              
              {/* Grid lines */}
              <div style={{ position: 'absolute', top: 24, left: 0, right: 200, bottom: 0, pointerEvents: 'none' }}>
                {[0.25, 0.5, 0.75].map(pct => (
                  <div key={pct} style={{ position: 'absolute', left: `${pct * 100}%`, top: 0, bottom: 0, borderLeft: '1px dashed #eee' }} />
                ))}
              </div>

              {/* Bars */}
              <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
                {sortedData.map((item, index) => {
                  const leftPct = ((item.startMs - minTime) / totalMs) * 100;
                  const widthPct = ((item.endMs - item.startMs) / totalMs) * 100;
                  
                  let bgColor = '#4dabf7';
                  if (item.isIncomplete) bgColor = '#ff6b6b';
                  else if (item.durationMin > 60) bgColor = '#ffa94d';
                  else if (item.durationMin > 30) bgColor = '#ffe066';

                  const formatDuration = (ms) => {
                    const sec = Math.floor(ms / 1000);
                    if (sec < 60) return `${sec}s`;
                    const min = Math.floor(sec / 60);
                    return `${min}m`;
                  };

                  return (
                    <div
                      key={item.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        height: 22,
                        marginBottom: 2,
                      }}
                    >
                      {/* Timeline area */}
                      <div style={{ flex: 1, position: 'relative', height: '100%' }}>
                        <MuiTooltip
                          title={
                            <span>
                              <b>{item.script_name}</b><br />
                              Start: {item.start_time}<br />
                              End: {item.end_time || '(incomplete)'}<br />
                              Duration: {formatDuration(item.durationMs)}
                            </span>
                          }
                          arrow
                          placement="top"
                        >
                          <div
                            style={{
                              position: 'absolute',
                              left: `${leftPct}%`,
                              width: `${Math.max(widthPct, 0.5)}%`,
                              minWidth: 6,
                              height: 16,
                              top: 3,
                              backgroundColor: bgColor,
                              borderRadius: 2,
                              cursor: 'pointer',
                              border: item.isIncomplete ? '1px dashed #c92a2a' : 'none',
                            }}
                          />
                        </MuiTooltip>
                      </div>
                      {/* Script name on right */}
                      <div style={{ width: 200, flexShrink: 0, paddingLeft: 8, fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.script_name} ({formatDuration(item.durationMs)})
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Summary */}
            <div style={{ fontSize: 10, color: '#999', marginTop: 8, textAlign: 'center' }}>
              Showing {sortedData.length} executions over {((maxTime - minTime) / 3600000).toFixed(1)} hours
            </div>
          </div>
        )}
      </div>
      <Button style={{ marginTop: 20 }} onClick={() => setScriptExecVisible(false)}>
        Close
      </Button>
    </Modal>
  );
};

export default ScriptExecutionTimeline;

